import {Box, Button, Input, Loading, Typography} from '@viptech/react-components'
import {enqueueSnackbar} from 'notistack'
import {useCallback, useEffect, useState} from 'react'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import useCompanies from 'src/hooks/api/companies/useCompanies'
import addIcon from 'src/images/addPlusIconWhiteSmall.svg'
import {ValidateUserPermissions} from '../../../../../common/utils/ValidatePermissions'
import RealodIconSmallChangeColor from '../../../../../images/ReloadIconGreenSmall'
import SearchIconInputSmallChangeColor from '../../../../../images/SearchIconInputSmall'
import api, {Client, Server} from '../../../../../services/api'
import handleErrorWithSnackbar from '../../../../../utilities/handleErrorWithSnackbar'

import useDebounce from 'src/hooks/useDebounce'
import Card from 'src/pages/camera-config/components/object-detection/components/stepperModal/common/components/Card'
import {StepperModalActionsButtonContainer} from 'src/pages/camera-config/components/object-detection/components/styles'
import {useCamRegisterModalContext2} from '../../../context/CameraRegisterContext'
import {ChosenObject} from '../CreateCameraStepper'
import {ContainerClientsCards, ContainerServers, ContainerServersCards, ContainerSteps, ContainerStepsRow, TransparentButton} from '../CreateCameraStepperBase'

type ServerList = {
  id: number
  name: string
}

type GeneralInformationStepProps = {
  nextFunction: (index: number, resume: string) => void
  returnFunction: (open: boolean) => void
  setChosen: (choices: Partial<ChosenObject>) => void
  chosen: ChosenObject | null
}

const GeneralInformationStep = (props: GeneralInformationStepProps) => {
  const {nextFunction, returnFunction, setChosen} = props

  const {setStep, setSelectedClient, setSelectedTypeIntegration} = useCamRegisterModalContext2()

  const {data: companies, isLoading: loadingCompany} = useCompanies()
  const [companySelected, setCompanySelected] = useState<DropdownItem>()

  const [clients, setClients] = useState<Client[]>([])
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [clientSelected, setClientSelected] = useState({id: '', label: ''})
  const [disableButton, setDisableButton] = useState(false)

  const hasPermissionReadClients = ValidateUserPermissions('clients:read')
  const [serversSearch, setServersSearch] = useState('')
  const debouncedServerSearch = useDebounce(serversSearch, 400)

  const loadClients = async () => {
    try {
      setIsLoadingClients(true)
      setServerSelected({id: '', label: ''})
      if (!companySelected) return setClients([])
      if (!hasPermissionReadClients) {
        enqueueSnackbar('Você não possui permissão de visualizar clientes. Entre em contato com o administrador', {variant: 'warning'})
        return
      }

      setClientSelected({id: '', label: ''})
      const response = await api.client.getMany({
        page: 1,
        paginate: true,
        filter: {companyIds: [Number(companySelected.id)]},
        search: {
          name: debouncedServerSearch,
        },
      })
      setClients(response.data.data.entities)
      const clients = response.data.data.entities

      if (!clients.length) {
        setDisableButton(true)
        enqueueSnackbar('A empresa selecionada não possui clientes cadastrados', {variant: 'warning'})
      } else {
        setDisableButton(false)
      }

      if (clients.length === 1) {
        setClientSelected({id: String(clients[0].id), label: clients[0].name})
        setSelectedClient(clients[0])
        setChosen({
          clientId: clients[0].id,
        })
      }
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao buscar os clientes')
    } finally {
      setIsLoadingClients(false)
    }
  }

  useEffect(() => {
    loadClients()
    loadAllServers()
  }, [companySelected, debouncedServerSearch])

  const [servers, setServers] = useState<Server[]>([])
  const [serverList, setServerList] = useState<ServerList[]>([])

  const hasPermissionReadServers = ValidateUserPermissions('servers:read')
  const hasRtspPermissionCreate = ValidateUserPermissions('rtsp-camera:create')

  const [serverSelected, setServerSelected] = useState({id: '', label: ''})

  const loadAllServers = useCallback(async () => {
    if (hasPermissionReadServers) {
      if (!companySelected) return
      try {
        setServerSelected({id: '', label: ''})
        const response = await api.server.findManyPaged({
          page: 1,
          pageSize: 100,
          filter: {
            integrationType: 'ENTRADA',
            companyIds: [Number(companySelected.id)],
          },
          includes: ['serverType'],
        })
        const servers = response.data.data.entities.map((it) => it.serverType)
        setServers(response.data.data.entities)
        if (servers.length === 1 && !hasRtspPermissionCreate) {
          checkServerList()
          setServerSelected({id: String(serverList[0].id), label: serverList[0].name})
          setChosen({
            serverName: serverList[0].name,
          })
        }

        if (!servers.length && !hasRtspPermissionCreate) {
          setDisableButton(true)
          enqueueSnackbar('Não encontramos servidores de entrada cadastrados para o cliente', {variant: 'warning'})
        } else {
          setDisableButton(false)
        }
      } catch (error) {
        handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao buscar servidores de integração')
      }
    } else enqueueSnackbar('Você não possui permissão de visualizar servidores. Entre em contato com o administrador', {variant: 'warning'})
  }, [companySelected])

  const checkServerList = () => {
    const list: ServerList[] = []
    if (servers.some((it) => it.serverTypeId === 2)) list.push({...list, id: 2, name: 'Digifort'})
    if (servers.some((it) => it.serverTypeId === 4)) list.push({...list, id: 4, name: 'D-Guard'})
    setServerList(list)
  }

  useEffect(() => {
    checkServerList()
  }, [servers])

  useEffect(() => {
    const initialCompanyToLoad = companies?.pages.flatMap((it) =>
      it.entities.map((company) => {
        return {
          id: String(company.id),
          label: company.name,
        }
      }),
    )

    if (initialCompanyToLoad?.length) {
      setCompanySelected(initialCompanyToLoad[0])
      setChosen({
        companyId: Number(initialCompanyToLoad[0].id),
      })
    }
  }, [companies])

  return (
    <ContainerSteps>
      <ContainerStepsRow>
        <Box display='flex' direction='column' width='50%'>
          <Box flex={1} align='center' justifyContent='center'>
            <Typography variant='span' size='16px' style={{fontWeight: 500}}>
              Empresa
            </Typography>
            <Typography variant='p' size='12px' color='#8E8E8E'>
              Selecione abaixo qual empresa irá receber a(s) câmera(s)
            </Typography>

            <Box paddingTop='20px'>
              <Dropdown
                fontSize='12px'
                isLoading={loadingCompany}
                items={
                  companies?.pages.flatMap((it) =>
                    it.entities.map((company) => {
                      return {
                        id: String(company.id),
                        label: company.name,
                      }
                    }),
                  ) ?? []
                }
                selected={companySelected}
                onChangeSelected={(e) => {
                  setChosen({
                    companyId: Number(e.id),
                  })
                  setCompanySelected(e)
                }}
              />
            </Box>
          </Box>

          <Box align='center' justifyContent='center'>
            <Typography variant='span' size='16px' style={{fontWeight: 500}}>
              Clientes
            </Typography>
            <Typography variant='p' size='12px' color='#8E8E8E'>
              Selecione abaixo qual cliente irá receber a(s) câmera(s)
            </Typography>

            <Box justifyContent='space-around' display='flex' align='center' margin='10px 0'>
              <TransparentButton color='#039855' onClick={loadClients}>
                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                  <RealodIconSmallChangeColor />
                  <span>Atualizar</span>
                </div>
              </TransparentButton>

              <TransparentButton color='#009EFF' onClick={() => window.open('/registers/clients?new')}>
                <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                  <img src={addIcon} alt='Adicionar cliente' />
                  <span>Adicionar cliente</span>
                </div>
              </TransparentButton>
            </Box>

            <Input.Root
              hasIcon
              maxLength={50}
              leftIcon={<SearchIconInputSmallChangeColor />}
              labelFontWeight='400'
              placeholder='Pesquisar'
              value={debouncedServerSearch}
              onChange={(e) => setServersSearch(e.target.value)}
            />

            <ContainerClientsCards>
              {isLoadingClients ? (
                <Loading color='#009EFF' size='25px' align='center' />
              ) : (
                clients.map((client) => {
                  return (
                    <Card
                      dispatchTo={client.name}
                      selected={clientSelected.label === client.name}
                      onClick={() => {
                        setClientSelected({id: String(client.id), label: client.name})
                        setSelectedClient(client)
                        setChosen({
                          companyId: client.companyId,
                          clientId: client.id,
                        })
                      }}
                      initialState={!clientSelected.label}
                    />
                  )
                })
              )}
            </ContainerClientsCards>
          </Box>
        </Box>

        <Box display='flex' direction='column' rowGap='25px' width='50%'>
          {clientSelected.label && (
            <Box align='center' justifyContent='center'>
              <Typography variant='span' size='16px' style={{fontWeight: 500}}>
                Integração de entrada
              </Typography>
              <Typography variant='p' size='12px' color='#8E8E8E'>
                Selecione o tipo de integração para se conectar à câmera
              </Typography>

              <Box justifyContent='space-around' width='100%' display='flex' align='center' margin='10px 0'>
                <TransparentButton color='#039855' onClick={loadAllServers}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                    <RealodIconSmallChangeColor />
                    <span>Atualizar</span>
                  </div>
                </TransparentButton>

                <TransparentButton color='#009EFF' onClick={() => window.open('/settings/inputs?new')}>
                  <div style={{display: 'flex', gap: '5px', justifyContent: 'flex-end'}}>
                    <img src={addIcon} alt='Adicionar integração' />
                    <span>Adicionar integração</span>
                  </div>
                </TransparentButton>
              </Box>

              <ContainerServers>
                <div>
                  {serverList.length !== 0 && (
                    <>
                      <Typography variant='span' size='14px' color='#5F5B5B' style={{fontWeight: 500}}>
                        VMS:
                      </Typography>
                      <ContainerServersCards>
                        {serverList.map((server) => {
                          return (
                            <Card
                              dispatchTo={server.name}
                              selected={serverSelected.label === server.name}
                              onClick={() => {
                                setServerSelected({id: String(server.id), label: server.name})
                                setChosen({
                                  serverName: server.name,
                                })
                              }}
                              initialState={!serverSelected.label}
                            />
                          )
                        })}
                      </ContainerServersCards>
                    </>
                  )}
                </div>

                <div>
                  {hasRtspPermissionCreate && (
                    <>
                      <Typography variant='span' size='14px' color='#5F5B5B' style={{fontWeight: 500}}>
                        Conexão direta:
                      </Typography>
                      <ContainerServersCards>
                        <Card
                          dispatchTo={'DVR / NVR'}
                          selected={serverSelected.label === 'DVR / NVR'}
                          onClick={() => {
                            setServerSelected({id: 'drv-nvr', label: 'DVR / NVR'})
                            setChosen({
                              serverName: 'DVR / NVR',
                            })
                          }}
                          initialState={!serverSelected.label}
                        />
                        <Card
                          dispatchTo={'Câmera RTSP'}
                          selected={serverSelected.label === 'Câmera RTSP'}
                          onClick={() => {
                            setChosen({
                              serverName: 'Câmera RTSP',
                            })
                            setServerSelected({id: 'rtsp', label: 'Câmera RTSP'})
                          }}
                          initialState={!serverSelected.label}
                        />
                        <Card
                          dispatchTo={'Câmera RTMP'}
                          selected={serverSelected.label === 'Câmera RTMP'}
                          onClick={() => {
                            setChosen({
                              serverName: 'Câmera RTMP',
                            })
                            setServerSelected({id: 'rtmp', label: 'Câmera RTMP'})
                          }}
                          initialState={!serverSelected.label}
                        />
                      </ContainerServersCards>
                    </>
                  )}
                </div>
              </ContainerServers>
            </Box>
          )}
        </Box>
      </ContainerStepsRow>

      <StepperModalActionsButtonContainer>
        <Button
          fontSize='12px'
          height='40px'
          width='150px'
          variant='outlined'
          color='#8E8E8E'
          onClick={() => {
            setClientSelected({id: '', label: ''})
            returnFunction(false)
          }}>
          Cancelar
        </Button>
        <Button
          fontSize='12px'
          disabledTextColor='#8E8E8E'
          height='40px'
          width='150px'
          onClick={() => {
            if (serverSelected.label === 'Digifort' || serverSelected.label === 'D-Guard') {
              setStep(2)
              setSelectedTypeIntegration(serverSelected.label)
            }
            nextFunction(1, 'Concluído')
          }}
          disabled={disableButton || !serverSelected.label}>
          Próximo
        </Button>
      </StepperModalActionsButtonContainer>
    </ContainerSteps>
  )
}

export default GeneralInformationStep
